import { L } from '@lib';
import { MasterBuildService } from '@services';
import type { Order } from '@services';
import { Select } from 'antd';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

interface IProps {
  masterBuildId?: string;
  value?: string[];
  onChange?: (value: string[]) => void;
  style?: React.CSSProperties;
}

/** The option label: the job's service as 'code category - name', dropping whichever of code or
 * category the service has not got, and falling back to the raw title when it has no services. */
const jobOptionLabel = (order: Order): string => {
  const services = order.serviceOrders ?? [];
  const dedupe = (values: (string | undefined)[]) =>
    Array.from(new Set(values.filter((v): v is string => !!v && v.trim().length > 0))).join(', ');

  const name = dedupe(services.map((so) => so.service?.name)) || order.title;
  const prefix = [
    dedupe(services.map((so) => so.service?.code)),
    dedupe(services.map((so) => so.service?.serviceGroup?.name)),
  ]
    .filter(Boolean)
    .join(' ');

  return prefix ? `${prefix} - ${name}` : name;
};

/**
 * Picks specific jobs on this build by name, rather than a keyword - the build page's "show only
 * these" filter. Options are fetched from the same endpoint the jobs list itself uses, searched by
 * title, so a build of a hundred legs never has to send them all down just to fill a dropdown.
 */
export const MasterBuildJobSelect: React.FC<IProps> = ({ masterBuildId, value, onChange, style }) => {
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);

  const fetchOptions = async (search: string) => {
    if (!masterBuildId) return;
    try {
      const result = await MasterBuildService.getOrders({
        masterBuildId,
        title: search || undefined,
        maxResultCount: 20,
        skipCount: 0,
      });
      setOptions(result.items.map((order) => ({ label: jobOptionLabel(order), value: order.id })));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchOptions('');
  }, [masterBuildId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = useMemo(() => debounce((text: string) => fetchOptions(text), 400), [masterBuildId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Select
      mode="multiple"
      showSearch
      allowClear
      filterOption={false}
      notFoundContent={null}
      maxTagCount="responsive"
      style={{ width: '100%', ...style }}
      value={value}
      options={options}
      onSearch={handleSearch}
      onChange={onChange}
      placeholder={L('Select jobs')}
    />
  );
};

export default MasterBuildJobSelect;
